import React, { useRef, useImperativeHandle, forwardRef, useState } from "react";
import { Author, ChangeSet, State, TrackedObject, getComponentLabel } from "@aidkitorg/typesheets/lib/collab";
import type { ConflictingOperation } from "@aidkitorg/typesheets/lib/collabMerge";
import { Button } from "@aidkitorg/component-library";
import { RenderDistro, findRenderableParent, ChangeBreadcrumb, isFullyAlive, type EditableDistroHandle } from "./ChangeUtils";


export type ConflictResolution = {
  choice: 'base' | 'incoming';
};

export type ConflictDisplayHandle = {
  /** Get the resolved events for this conflict, with bumped Lamport timestamps. */
  getResolvedEvents: () => ChangeSet | undefined;
};

/**
 * Finds the best object to render for a conflict.
 * For primitives (like a label string), walks up to the nearest renderable parent component
 * (e.g., the "Text Entry" that contains the label).
 * Returns both the renderable object and whether it's the root.
 */
function findRenderableObject(state: State, objectId: string, parentState?: State): { object: TrackedObject | undefined, isRoot: boolean } {
  const obj = state.workingSet[objectId];
  if (obj) {
    const renderable = findRenderableParent(obj, parentState);
    if (renderable) return { object: renderable, isRoot: renderable.id === 'root' };
  }

  // Object is absent or pruned (empty after compression on the deleted side of a conflict) —
  // fall back to parentState where the object still has full content.
  if (parentState) {
    const parentObj = parentState.workingSet[objectId];
    if (parentObj) {
      const renderable = findRenderableParent(parentObj);
      if (renderable) return { object: renderable, isRoot: renderable.id === 'root' };
      return { object: parentObj, isRoot: false };
    }
  }

  if (!obj) return { object: undefined, isRoot: false };
  return { object: obj, isRoot: false };
}

/**
 * Gets a human-readable label for the conflicting field by walking up the parent
 * hierarchy to build a path like "TextEntry → content → en".
 */
function getConflictFieldLabel(state: State, objectId: string): string {
  const obj = state.workingSet[objectId];
  if (!obj) return objectId.substring(0, 8);

  // Collect property names as we walk up, until we hit a component with a kind.
  const propPath: string[] = [];
  let current: TrackedObject | undefined = obj;

  while (current?.parent) {
    const parent: TrackedObject = current.parent;

    // Track the property name that links current to parent
    if (parent.value && typeof parent.value === 'object' && !Array.isArray(parent.value)) {
      for (const [key, val] of Object.entries(parent.value)) {
        if ((val as TrackedObject)?.id === current.id) {
          propPath.unshift(key);
          break;
        }
      }
    }

    const parentKind = parent.read()?.kind;
    if (parentKind) {
      const label = getComponentLabel(parent);
      const componentLabel = label ? `${parentKind}: ${label}` : parentKind;
      return propPath.length > 0 ? `${componentLabel} → ${propPath.join(' → ')}` : componentLabel;
    }

    current = parent;
  }

  return objectId.substring(0, 8);
}

export const ConflictDisplay = forwardRef<ConflictDisplayHandle, {
  conflict: ConflictingOperation;
  baseState: State;
  incomingState: State;
  parentState?: State;
  baseLabel: string;
  incomingLabel: string;
  author?: Author;
  resolution: ConflictResolution | undefined;
  onResolve: (resolution: ConflictResolution) => void;
  onDeselect?: () => void;
}>(function ConflictDisplay(props, ref) {
      const { conflict, baseState, incomingState, parentState, baseLabel, incomingLabel, author, resolution, onResolve, onDeselect } = props;
      const baseEditRef = useRef<EditableDistroHandle>(null);
      const incomingEditRef = useRef<EditableDistroHandle>(null);

      // Build resolved events with bumped Lamport timestamps so resolutions survive future rebases
      useImperativeHandle(ref, () => ({
        getResolvedEvents: () => {
          if (!resolution) return undefined;
          const resolvedPrevGen = Math.max(
            conflict.baseChildWinningOp?.prev_gen ?? 0,
            conflict.incomingChildWinningOp?.prev_gen ?? 0,
          ) + 1;

          // Check if the user edited the chosen side — returns only the user's actual delta ops
          const editRef = resolution.choice === 'base' ? baseEditRef : incomingEditRef;
          const editedEvents = editRef.current?.getEditedEvents();
          if (editedEvents?.length) {
            // Shift ops up so they beat both sides, preserving relative ordering
            const minEditedPrevGen = Math.min(...editedEvents.map(([,, op]) => op?.prev_gen ?? 0));
            const offset = minEditedPrevGen < resolvedPrevGen ? resolvedPrevGen - minEditedPrevGen : 0;
            return editedEvents.map(([objectId, kind, op]) => [
              objectId,
              kind,
              op ? { ...op, prev_gen: op.prev_gen + offset, ...(author ? { author } : {}) } : op,
            ] as ChangeSet[number]);
          }

          // No edits — use the chosen side's winning op as the resolution, but with a bumped prev_gen (and author if available)
          const chosenOp = resolution.choice === 'base' ? conflict.baseChildWinningOp : conflict.incomingChildWinningOp;
          return [[conflict.objectId, conflict.kind, { ...chosenOp, prev_gen: resolvedPrevGen, ...(author ? { author } : {}) }]] as ChangeSet;
        }
      }));

      // Find renderable parent components for the conflicting object
      const { object: baseRenderObject, isRoot: baseIsRoot } = findRenderableObject(baseState, conflict.objectId, parentState);
      const { object: incomingRenderObject, isRoot: incomingIsRoot } = findRenderableObject(incomingState, conflict.objectId, parentState);

      const fieldLabel = getConflictFieldLabel(baseState, conflict.objectId) || getConflictFieldLabel(incomingState, conflict.objectId);

      // Build changesToRender so Distro auto-expands collapsed sections and highlights the conflicting object.
      // If the object was deleted on one side (not renderable from that state), show it as a removal (red)
      // using content from parentState; otherwise show as an addition (green).
      const baseObj = baseState.workingSet[conflict.objectId];
      const baseRenderable = baseObj && isFullyAlive(baseObj)
        ? findRenderableParent(baseObj, parentState) : undefined;
      const baseChanges = baseRenderable
        ? { additions: [{ object: baseObj }], removals: [] }
        : { additions: [], removals: parentState?.workingSet[conflict.objectId]
          ? [{ object: parentState.workingSet[conflict.objectId] }] : [] };

      const incomingObj = incomingState.workingSet[conflict.objectId];
      const incomingRenderable = incomingObj && isFullyAlive(incomingObj)
        ? findRenderableParent(incomingObj, parentState) : undefined;
      const incomingChanges = incomingRenderable
        ? { additions: [{ object: incomingObj }], removals: [] }
        : { additions: [], removals: parentState?.workingSet[conflict.objectId]
          ? [{ object: parentState.workingSet[conflict.objectId] }] : [] };

      const handleChoose = (choice: 'base' | 'incoming') => {
        onResolve({ choice });
      };

      const handleEdit = () => {
        if (resolution) onDeselect?.();
      };

      return (
        <div className="border rounded-lg p-4 bg-white shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <span className={`inline-block w-3 h-3 rounded-full ${resolution ? 'bg-green-500' : 'bg-yellow-500'}`} />
            <span className="font-medium text-sm">{fieldLabel}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              conflict.conflictType === 'patch' ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'
            }`}>
              {conflict.conflictType}
            </span>
          </div>

          {/* Side-by-side */}
          <div className="grid grid-cols-2 gap-4 mt-3">
            {/* Base side */}
            <ConflictSide
              label={baseLabel}
              labelColor="text-blue-700"
              ringColor="ring-blue-400"
              isSelected={resolution?.choice === 'base'}
              onChoose={() => handleChoose('base')}
              onEdit={handleEdit}
              editRef={baseEditRef}
              renderObject={baseRenderObject}
              isRoot={baseIsRoot}
              editable={!!baseRenderable}
              changesToRender={baseChanges}
              breadcrumbNode={<ChangeBreadcrumb obj={baseState.workingSet[conflict.objectId]} isRoot={false} objId={conflict.objectId} parentState={parentState} />}
            />

            {/* Incoming side */}
            <ConflictSide
              label={incomingLabel}
              labelColor="text-amber-700"
              ringColor="ring-amber-400"
              isSelected={resolution?.choice === 'incoming'}
              onChoose={() => handleChoose('incoming')}
              onEdit={handleEdit}
              editRef={incomingEditRef}
              renderObject={incomingRenderObject}
              isRoot={incomingIsRoot}
              editable={!!incomingRenderable}
              changesToRender={incomingChanges}
              breadcrumbNode={<ChangeBreadcrumb obj={incomingState.workingSet[conflict.objectId]} isRoot={false} objId={conflict.objectId} parentState={parentState} />}
            />
          </div>
        </div>
      );
    });

function ConflictSide(props: {
  label: string;
  labelColor: string;
  ringColor: string;
  isSelected: boolean;
  onChoose: () => void;
  onEdit?: () => void;
  editRef: React.RefObject<EditableDistroHandle>;
  renderObject: TrackedObject | undefined;
  isRoot: boolean;
  editable?: boolean;
  changesToRender?: { additions: { object: TrackedObject }[], removals: { object: TrackedObject }[] };
  breadcrumbNode?: React.ReactNode;
}) {
  const { label, labelColor, ringColor, isSelected, onChoose, onEdit, editRef, renderObject, isRoot, editable = true, changesToRender, breadcrumbNode } = props;
  const [hasEdits, setHasEdits] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className={`text-sm font-medium ${labelColor}`}>{label}</span>
        <div className="flex items-center gap-2">
          {hasEdits && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => { setResetKey(k => k + 1); setHasEdits(false); }}
            >
              Reset
            </Button>
          )}
          <Button
            size="sm"
            variant={isSelected ? 'primary' : 'secondary'}
            onClick={onChoose}
          >
            {isSelected ? 'Selected' : 'Use This'}
          </Button>
        </div>
      </div>
      {breadcrumbNode}
      {renderObject ? (
        <div className={`rounded ${isSelected ? `ring-2 ${ringColor}` : ''}`}>
          <RenderDistro
            key={resetKey}
            ref={editRef}
            object={renderObject}
            parentIsRoot={isRoot}
            editable={editable}
            changes={changesToRender}
            onEditStateChange={(edited) => {
              setHasEdits(edited);
              if (edited) onEdit?.();
            }}
          />
        </div>
      ) : null}
    </div>
  );
}
